import { Injectable } from '@angular/core';
import { Category } from '@vendasta/google-analytics';
import moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { alignLocalDateToUTC, getDateBoundaries, previousTimePeriod } from '../../../core/metrics.helper';
import {
  Alignment,
  Calendar,
  CompositeFilter_Operator,
  FieldFilter_Operator,
  Operator,
  Type,
} from '../../../core/multi_location_analytics_sdk/_internal/enums';
import {
  Aggregate,
  AlignmentPeriod,
  DateRange,
  Measure,
  QueryMetricsRequest,
  QueryMetricsResponse,
} from '../../../core/multi_location_analytics_sdk/_internal/objects';
import { MultiLocationAnalyticsService } from '../../../core/multi_location_analytics_sdk/multi-location-analytics.service';
import { Site, SiteService } from '../../../core/site';
import { GaRecordMap } from '../../ga-metrics/ga-metrics.interface';
import { ChartEntry } from '../../metrics';

@Injectable()
export class GaOverviewMetricsService {
  loadingGARecords$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loadingGARecords$: Observable<boolean> = this.loadingGARecords$$.asObservable();
  loadingGARecordsError$$: BehaviorSubject<boolean> = new BehaviorSubject<any>(false);
  loadingGARecordsError$: Observable<boolean> = this.loadingGARecordsError$$.asObservable();
  gaRecords$$: BehaviorSubject<[GaRecordMap, GaRecordMap]> = new BehaviorSubject<[GaRecordMap, GaRecordMap]>(undefined);
  gaRecords$: Observable<[GaRecordMap, GaRecordMap]> = this.gaRecords$$.asObservable();

  site$: Observable<Site>;

  constructor(private multiLocationAnalyticsService: MultiLocationAnalyticsService, private siteService: SiteService) {
    // this week VS last week
    const [start, end] = getDateBoundaries('7days');
    const prevRange = previousTimePeriod(moment(start), moment(end));

    this.site$ = this.siteService.site$;
    this.site$
      .pipe(
        tap(() => {
          this.loadingGARecords$$.next(true);
        }),
        switchMap((site: Site) => {
          return combineLatest([
            this.queryAndParseGARecord(
              site.partnerId,
              site.accountId,
              moment(start).startOf('day'),
              moment(end).endOf('day'),
            ),
            this.queryAndParseGARecord(
              site.partnerId,
              site.accountId,
              moment(prevRange[0]).startOf('day'),
              moment(prevRange[1]).endOf('day'),
            ),
          ]);
        }),
        catchError(() => {
          this.loadingGARecordsError$$.next(true);
          this.loadingGARecords$$.next(false);
          return of(null);
        }),
      )
      .subscribe((response) => {
        this.gaRecords$$.next(response);
        this.loadingGARecords$$.next(false);
      });
  }

  queryAndParseGARecord(
    partnerId: string,
    accountId: string,
    start: moment.Moment,
    end: moment.Moment,
  ): Observable<GaRecordMap> {
    return this.multiLocationAnalyticsService
      .queryMetrics(this.getGARecordsRequest(partnerId, accountId, start, end))
      .pipe(
        map((resp: QueryMetricsResponse) => {
          const metrics = resp.metricResults[0].metrics.metrics;
          if (!metrics) {
            return {};
          }
          const recordsForTotalVisitors: ChartEntry[] = [];
          const recordsForAvgSecondsOnWeb: ChartEntry[] = [];
          metrics.map((m) => {
            recordsForTotalVisitors.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[0],
            } as ChartEntry);
            recordsForAvgSecondsOnWeb.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[1],
            } as ChartEntry);
          });
          return {
            totalVisitors: recordsForTotalVisitors,
            averageSecondsOnWebsite: recordsForAvgSecondsOnWeb,
          } as GaRecordMap;
        }),
        shareReplay(1),
      );
  }

  getGARecordsRequest(
    partnerId: string,
    accountId: string,
    startDate: moment.Moment,
    endDate: moment.Moment,
  ): QueryMetricsRequest {
    const req: QueryMetricsRequest = this.getBaseRequest(partnerId, accountId);
    req.alignmentPeriod = new AlignmentPeriod({
      calendar: Calendar.CALENDAR_YEAR,
    });
    req.alignment = Alignment.ALIGN_DELTA;
    req.dateRange = new DateRange({
      start: startDate.toDate(),
      end: endDate.toDate(),
    });
    req.measures = [
      new Measure({
        aggregate: new Aggregate({
          measure: 'total_visitors',
          aggOp: Operator.SUM,
        }),
      }),
      new Measure({
        aggregate: new Aggregate({
          measure: 'average_seconds_on_website',
          aggOp: Operator.AVG,
        }),
      }),
    ];
    return req;
  }

  getBaseRequest(partnerId: string, accountId: string): QueryMetricsRequest {
    return new QueryMetricsRequest({
      partnerId: partnerId,
      metricName: 'google_analytics_v2_record',
      resourceIds: [
        {
          businessId: { businessId: accountId },
        },
      ],
      filter: {
        compositeFilter: {
          op: CompositeFilter_Operator.AND,
          filters: [
            {
              fieldFilter: {
                dimension: 'category',
                operator: FieldFilter_Operator.EQUAL,
                value: {
                  valueType: Type.PROPERTY_TYPE_STRING,
                  value: Category[Category.CATEGORY_WEBSITE],
                },
              },
            },
            {
              fieldFilter: {
                dimension: 'subcategory',
                operator: FieldFilter_Operator.EQUAL,
                value: {
                  valueType: Type.PROPERTY_TYPE_STRING,
                  value: Category[Category.CATEGORY_WEBSITE],
                },
              },
            },
          ],
        },
      },
    });
  }
}
